import { PaperPlaneRight, Plus, X } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import {
  FileAttachment,
  FileAttachments,
  useAttachments,
} from "features/attachments";
import { VoiceRecording } from "features/voice-message/components";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import ProfileImage from "common/components/ui/ProfileImage";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useFileUpload from "../../../datahooks/useFileUpload";
import useActiveConversationOwnerAccount from "../../../hooks/useActiveConversationOwnerAccount";
import useInsertText from "../../../hooks/useInsertText";
import useMessageField from "../../../hooks/useMessageField";
import { useNewConversation } from "../../../hooks/useNewConversation";
import EditMessageActions from "./EditMessageActions";
import MessageOptions from "./MessageOptions";
import RecordButton from "./RecordButton";
import SettingsPopover from "./SettingsPopover";

import type { ConversationMessage } from "../../../types";

interface MessageFieldProps {
  editMessage: ConversationMessage;
  cancelEdit: () => void;
  hasMessageFromAnotherUser: boolean;
  scrollToBottom: () => void;
}

export default function MessageField({
  editMessage,
  cancelEdit,
  hasMessageFromAnotherUser,
  scrollToBottom,
}: MessageFieldProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { activeConversation } = useActiveConversationContext();
  const { newConversation } = useNewConversation();

  const [isMessageActionsOpen, setIsMessageActionsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const shouldFocusRef = useRef(false);
  const prevEditMessageRef = useRef<ConversationMessage | null>(null);
  const messageFieldRef = useRef<HTMLTextAreaElement>(null);

  const {
    formReturn: { setValue, watch, getValues, reset, register },
    onSubmit,
    onKeyDown,
    isLoading,
    ctrlToSend,
    setCtrlToSend,
    isMessagingDisabled,
  } = useMessageField(hasMessageFromAnotherUser, editMessage, () => {
    if (editMessage) {
      cancelEdit();
    } else {
      scrollToBottom();
      shouldFocusRef.current = true;
    }
    reset({ message: "" });
  });

  const insertText = useInsertText(messageFieldRef, (string) =>
    setValue("message", string),
  );
  const { ownerAccountId, ownerAccount } = useActiveConversationOwnerAccount();

  const { uploadFile, isUploadingAnyFiles } = useFileUpload({
    accountId: ownerAccountId,
  });

  const {
    attachments,
    onUpload,
    onUploadCancel,
    removeAttachment,
    resetAttachments,
  } = useAttachments([], async (file, abortSignal) => {
    const {
      file_identifier,
      file_input: { size, originalname, mimetype },
    } = await uploadFile({ file, filename: file.name, abortSignal });

    const fileUrl = URL.createObjectURL(file);

    const existingAttachments = getValues("attachments");
    const uploadedAttachment = {
      id: file_identifier,
      name: originalname,
      media_type: mimetype,
      url: fileUrl,
      byte_size: size,
    };
    setValue(
      "attachments",
      existingAttachments
        ? [...existingAttachments, uploadedAttachment]
        : [uploadedAttachment],
      { shouldDirty: true },
    );

    return {
      id: file_identifier,
      byteSize: size,
      name: originalname,
      mimeType: mimetype,
      url: fileUrl,
    };
  });

  useEffect(() => {
    if (editMessage !== prevEditMessageRef.current) {
      reset({ message: editMessage?.body });
      prevEditMessageRef.current = editMessage;
    }
  }, [editMessage, reset]);

  useEffect(() => {
    setIsRecording(false);
    resetAttachments();
  }, [newConversation, activeConversation]);

  useEffect(() => {
    if (shouldFocusRef.current) {
      messageFieldRef.current?.focus();
      shouldFocusRef.current = false;
    }
  }, [isMessagingDisabled]);

  function onRemoveAttachment(attachmentId: string) {
    setValue(
      "attachments",
      getValues("attachments").filter(({ id }) => id !== attachmentId),
      { shouldDirty: true },
    );
    removeAttachment(attachmentId);
  }

  let textAreaPlaceholder = "Write your message here";

  if (isLoading) {
    textAreaPlaceholder = "Sending message";
  } else if (isMessagingDisabled) {
    textAreaPlaceholder = "Cannot message this user";
  }

  const { ref, ...registerRest } = register("message");

  const isEmptyMessage = !(
    watch("message")?.trim() ||
    attachments.length ||
    watch("audio")
  );

  const messageOptions = (
    <MessageOptions
      isMessagingDisabled={isMessagingDisabled}
      uploadedFilesCount={attachments.length}
      ctrlToSend={ctrlToSend}
      setCtrlToSend={setCtrlToSend}
      onFileUpload={onUpload}
      insertText={insertText}
      isRecording={isRecording}
      setIsRecording={setIsRecording}
    />
  );

  const sendButton = (
    <Button
      variant="default-purple"
      intent={isDesktop ? "default" : "iconOnly"}
      isLoading={isLoading}
      disabled={isEmptyMessage || isMessagingDisabled || isUploadingAnyFiles}
      leftIcon={isDesktop && <PaperPlaneRight />}
      onClick={onSubmit}
      size={isDesktop ? "sm" : "md"}
    >
      {isDesktop ? "Send" : <PaperPlaneRight />}
    </Button>
  );

  const voiceRecordingComponent = (
    <VoiceRecording
      onRecordingEnd={(audioBlob) => {
        setValue("audio", audioBlob);
      }}
      onRecordingDelete={() => {
        setIsRecording(false);
        reset({ message: "" });
      }}
    />
  );

  return (
    <>
      <div
        className={clsx(
          isMessagingDisabled
            ? "text-black-500 lg:bg-black-100"
            : "text-black-950 lg:bg-white",
          "flex w-full items-center gap-2 rounded-t-3xl border border-b-0 border-white lg:flex-col lg:items-stretch lg:gap-0 lg:rounded-3xl lg:border-b lg:border-black-200",
          "has-[textarea:hover:focus-within]:bg-white",
          "p-2 has-[textarea:focus-visible]:border-purple-400",
          !isDesktop && isRecording && "justify-between border-purple-400",
        )}
      >
        <RenderIf condition={!isDesktop && !isRecording}>
          <Button
            variant={isMessageActionsOpen ? "secondary-black" : "ghost-black"}
            intent="iconOnly"
            onClick={() => setIsMessageActionsOpen(!isMessageActionsOpen)}
          >
            {isMessageActionsOpen ? <X /> : <Plus />}
          </Button>
        </RenderIf>

        {!isDesktop && isRecording ? (
          voiceRecordingComponent
        ) : (
          <div className="flex w-full flex-col gap-2 overflow-x-hidden rounded-5xl bg-black-100 p-2 lg:gap-0 lg:rounded-none lg:bg-transparent lg:p-0">
            <div className="flex flex-row gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <ProfileImage
                    size="xs"
                    className="mt-0.5"
                    src={ownerAccount?.picture_url}
                  />
                </TooltipTrigger>

                {ownerAccount && (
                  <TooltipContent className="w-fit">
                    {ownerAccount.full_name}
                  </TooltipContent>
                )}
              </Tooltip>

              {isRecording && isDesktop ? (
                voiceRecordingComponent
              ) : (
                <TextareaAutosize
                  id="inbox-text-area"
                  minRows={isDesktop ? 2 : 1}
                  maxRows={11.3}
                  disabled={isMessagingDisabled}
                  placeholder={textAreaPlaceholder}
                  onKeyDown={onKeyDown}
                  className="flex-1 resize-none bg-transparent py-1 outline-none scrollbar-thin text-sm"
                  ref={(el) => {
                    ref(el);
                    messageFieldRef.current = el;
                  }}
                  {...registerRest}
                />
              )}
            </div>

            <RenderIf condition={!!attachments.length}>
              <FileAttachments
                attachments={attachments}
                renderAttachment={(attachment) => {
                  const props =
                    "file" in attachment
                      ? {
                          file: attachment.file,
                          errorMessage:
                            "errorMessage" in attachment &&
                            attachment.errorMessage,
                          onRemove: () => {
                            onUploadCancel(attachment.file);
                          },
                        }
                      : {
                          fileData: {
                            name: attachment.name,
                            byteSize: attachment.byteSize,
                            mimeType: attachment.mimeType,
                            url: attachment.url,
                          },
                          onRemove: () => {
                            onRemoveAttachment(attachment.id);
                          },
                        };

                  return <FileAttachment {...props} />;
                }}
              />
            </RenderIf>
          </div>
        )}

        {(isEmptyMessage || isMessagingDisabled) &&
        !isDesktop &&
        !isRecording ? (
          <RecordButton
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            isDisabled={isMessagingDisabled}
          />
        ) : (
          <RenderIf condition={!isDesktop}>{sendButton}</RenderIf>
        )}

        {/* Note: Add padding right on the desktop because of the Intercom widget overlap */}
        <div className="mt-2 hidden flex-row items-center justify-between border-t border-black-200 pt-2 lg:flex">
          {messageOptions}

          {editMessage ? (
            <EditMessageActions
              isUpdatingMessage={isLoading}
              onCancel={cancelEdit}
              onConfirm={onSubmit}
            />
          ) : (
            <div className="flex flex-row items-center justify-between gap-2">
              {/* Settings Popover */}
              <SettingsPopover
                ctrlToSend={ctrlToSend}
                setCtrlToSend={setCtrlToSend}
                isDisabled={isMessagingDisabled}
              />

              <RenderIf condition={isDesktop}>{sendButton}</RenderIf>
            </div>
          )}
        </div>
      </div>

      <RenderIf condition={!isDesktop && isMessageActionsOpen && !isRecording}>
        <div className="border-t border-black-200 p-2">{messageOptions}</div>
      </RenderIf>
    </>
  );
}
